import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BrandLogo from '../components/BrandLogo';
import SignaturePad from '../components/SignaturePad';
import PublicAnalyticsBeacon from '../components/PublicAnalyticsBeacon';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { FileDown, ShieldCheck, CheckCircle2, Lock, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { computeAddons, computeOption } from './proposals/compute';

const GBP = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(Number(v || 0));
const FREQUENCY = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually', annual_upfront: 'Annual upfront' };

export default function PublicProposal() {
    const { token } = useParams();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [shouldRedirectToNarrative, setShouldRedirectToNarrative] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedHosting, setSelectedHosting] = useState(null);
    const [acceptance, setAcceptance] = useState({
        signer_name: '', signer_title: '', signer_email: '', signer_company: '',
        typed_signature: '', signature_image_base64: null, accept_terms: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [acceptedResult, setAcceptedResult] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const forceStandard = params.get('view') === 'standard';
        axios.get(`${API_BASE}/public/proposals/${token}`)
            .then(r => {
                // If a narrative version exists, the narrative URL is the canonical
                // client-facing page. Redirect there (preserving any query string)
                // unless the user explicitly opted into the standard view via ?view=standard.
                const hasNarrative = !!r.data?.proposal?.narrative
                    && Object.keys(r.data.proposal.narrative).length > 0;
                if (hasNarrative && !forceStandard) {
                    setShouldRedirectToNarrative(true);
                    return;
                }
                setData(r.data);
                // Pre-select via ?option=<id> query param (deep-link from narrative viewer)
                const optParam = params.get('option');
                if (r.data.proposal.accepted_option_id) {
                    setSelectedOption(r.data.proposal.accepted_option_id);
                } else if (optParam && (r.data.proposal.options || []).some(o => o.id === optParam)) {
                    setSelectedOption(optParam);
                }
                if (r.data.proposal.accepted_hosting_option_id) {
                    setSelectedHosting(r.data.proposal.accepted_hosting_option_id);
                } else {
                    const visible = (r.data.proposal.hosting_options || []).filter(h => h.visible_on_proposal !== false);
                    if (visible.length > 0) setSelectedHosting(visible[0].id);
                }
                setAcceptance(a => ({ ...a, signer_company: r.data.client?.company_name || '' }));
                // Auto-scroll to acceptance form when arriving from the narrative CTA
                if (optParam) {
                    setTimeout(() => {
                        const form = document.querySelector('[data-testid="acceptance-form"]');
                        if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 350);
                }
            })
            .catch(e => setErr(e?.response?.status === 404 ? 'This proposal is not available.' : 'Unable to load proposal.'));
    }, [token, location.search]);

    if (shouldRedirectToNarrative) {
        return <Navigate to={`/p/${token}/narrative${location.search || ''}`} replace />;
    }

    if (err) return <CenterMsg><BrandLogo variant="short" /><div className="mt-4 text-[color:var(--bm-text-muted)]">{err}</div></CenterMsg>;
    if (!data) return <CenterMsg><span className="text-[color:var(--bm-text-muted)]">Loading…</span></CenterMsg>;

    const { proposal, client, modules, settings } = data;
    const isAccepted = proposal.status === 'accepted' || !!acceptedResult;
    const isExpired = proposal.status === 'expired';

    const hostingOptions = (proposal.hosting_options || []).filter(h => h.visible_on_proposal !== false);
    const chosenHosting = hostingOptions.find(h => h.id === selectedHosting) || null;

    // Ad-hoc add-ons (baked in globally by admin) + hosting add-ons (client pick)
    const adHocAddons = computeAddons(proposal.ad_hoc_modules || []);
    const hostingSetup = chosenHosting && chosenHosting.include_in_totals !== false ? Number(chosenHosting.setup_fee || 0) : 0;
    const hostingMonthly = chosenHosting && chosenHosting.include_in_totals !== false ? Number(chosenHosting.monthly_support_fee || 0) : 0;
    const combinedAddons = {
        extra_setup: (adHocAddons.extra_setup || 0) + hostingSetup,
        extra_monthly: (adHocAddons.extra_monthly || 0) + hostingMonthly,
    };
    const vatRate = Number(settings?.default_vat_rate || 20);

    const submit = async (e) => {
        e.preventDefault();
        if (!selectedOption) return toast.error('Please choose a commercial option to accept.');
        if (hostingOptions.length > 0 && !selectedHosting) return toast.error('Please choose a hosting option.');
        if (!acceptance.signer_name.trim()) return toast.error('Please enter your full name.');
        if (!acceptance.signer_email.trim()) return toast.error('Please enter your email.');
        if (!acceptance.typed_signature.trim()) return toast.error('Please type your signature.');
        if (!acceptance.accept_terms) return toast.error('Please tick to accept the terms.');
        setSubmitting(true);
        try {
            const r = await axios.post(`${API_BASE}/public/proposals/${token}/accept`, {
                option_id: selectedOption,
                hosting_option_id: selectedHosting,
                signer_name: acceptance.signer_name.trim(),
                signer_title: acceptance.signer_title.trim() || null,
                signer_email: acceptance.signer_email.trim(),
                signer_company: acceptance.signer_company.trim() || null,
                typed_signature: acceptance.typed_signature.trim(),
                signature_image_base64: acceptance.signature_image_base64,
                accept_terms: true,
            });
            setAcceptedResult(r.data);
            setData(d => ({
                ...d,
                proposal: {
                    ...d.proposal,
                    status: 'accepted',
                    accepted_option_id: selectedOption,
                    accepted_hosting_option_id: selectedHosting,
                    accepted_at: new Date().toISOString(),
                    acceptance: {
                        signer_name: acceptance.signer_name.trim(),
                        signer_company: acceptance.signer_company.trim() || null,
                        accepted_at: new Date().toISOString(),
                    },
                },
            }));
            toast.success('Proposal accepted — welcome aboard.');
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Unable to submit acceptance';
            toast.error(typeof msg === 'string' ? msg : 'Unable to submit acceptance');
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-[color:var(--bm-bg)]" data-testid="public-proposal-page">
            <PublicAnalyticsBeacon token={token} viewKind="summary" />
            {/* Header */}
            <header className="bg-white border-b border-[color:var(--bm-border)] sticky top-0 z-10">
                <div className="max-w-[1060px] mx-auto px-6 py-4 flex items-center justify-between">
                    <BrandLogo variant="short" />
                    <a href={`${API_BASE}/public/proposals/${token}/pdf`} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[color:var(--bm-border)] text-[13px] font-medium text-[color:var(--bm-primary)] bg-white hover:bg-[color:var(--bm-bg-mute)] transition"
                       data-testid="public-pdf-link">
                        <FileDown size={15} /> Download PDF
                    </a>
                </div>
            </header>

            <main className="max-w-[1060px] mx-auto px-6 py-10 bm-fade-in">
                {/* Status banner */}
                {isAccepted && <AcceptedBanner result={acceptedResult} token={token} proposal={proposal} />}
                {isExpired && <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[13.5px]">
                    <Clock size={16} className="inline mr-1.5" /> This proposal expired on {proposal.valid_until}. Please contact us to request a revised proposal.
                </div>}

                {/* Intro */}
                <div className="bg-white rounded-2xl border border-[color:var(--bm-border)] p-8 md:p-10 mb-8" style={{ borderTop: '4px solid var(--bm-accent)' }}>
                    <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[color:var(--bm-accent-600)]">Proposal · {proposal.proposal_number}</div>
                    <h1 className="font-display text-[30px] md:text-[36px] font-semibold text-[color:var(--bm-primary)] mt-2 leading-tight">{proposal.title}</h1>
                    <div className="mt-5 flex flex-wrap gap-8 text-[13px] text-[color:var(--bm-text-muted)]">
                        <Meta label="Prepared for" value={client.company_name} sub={client.main_contact_name} />
                        <Meta label="Proposal date" value={proposal.proposal_date || '—'} />
                        <Meta label="Valid until" value={proposal.valid_until || '—'} />
                    </div>
                    {proposal.scope_summary && <p className="mt-6 text-[14.5px] leading-relaxed text-[color:var(--bm-text)] max-w-3xl">{proposal.scope_summary}</p>}
                </div>

                {/* Modules */}
                {modules.length > 0 && (
                    <section className="mb-10">
                        <SectionTitle>Included Modules</SectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {modules.map(m => (
                                <div key={m.id} className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                                    <div className="font-display text-[15px] font-semibold text-[color:var(--bm-text)]">{m.name}</div>
                                    {m.description && <div className="text-[13px] text-[color:var(--bm-text-muted)] mt-1">{m.description}</div>}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Ad-hoc bespoke items */}
                {(proposal.ad_hoc_modules || []).length > 0 && (
                    <section className="mb-10">
                        <SectionTitle>Additional Items</SectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {proposal.ad_hoc_modules.map((m, idx) => (
                                <div key={m.id || idx} className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid={`public-ad-hoc-${idx}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-display text-[15px] font-semibold text-[color:var(--bm-text)]">{m.name}</div>
                                        {(m.suggested_setup_price > 0 || m.suggested_monthly_price > 0) && m.include_in_pricing !== false && (
                                            <div className="text-right shrink-0">
                                                {m.suggested_setup_price > 0 && <div className="text-[11px] text-[color:var(--bm-text-muted)]">{GBP(m.suggested_setup_price)} setup</div>}
                                                {m.suggested_monthly_price > 0 && <div className="text-[11px] text-[color:var(--bm-text-muted)]">{GBP(m.suggested_monthly_price)}/mo</div>}
                                            </div>
                                        )}
                                    </div>
                                    {m.description && <div className="text-[13px] text-[color:var(--bm-text-muted)] mt-1">{m.description}</div>}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Hosting & Access — client-selectable */}
                {hostingOptions.length > 0 && (
                    <HostingPicker
                        options={hostingOptions}
                        selectedId={selectedHosting}
                        onSelect={setSelectedHosting}
                        locked={isAccepted || isExpired}
                        settings={settings}
                    />
                )}

                {/* Options */}
                <section className="mb-10">
                    <SectionTitle>{isAccepted ? 'Accepted Option' : 'Choose a Commercial Option'}</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(proposal.options || [])
                            .filter(o => !isAccepted || o.id === proposal.accepted_option_id)
                            .map(opt => (
                                <PublicOptionCard
                                    key={opt.id} opt={opt}
                                    selected={selectedOption === opt.id}
                                    onSelect={() => !isAccepted && !isExpired && setSelectedOption(opt.id)}
                                    locked={isAccepted || isExpired}
                                    addons={combinedAddons}
                                    vatRate={vatRate}
                                    hostingLine={chosenHosting}
                                    proposalDate={proposal.proposal_date}
                                />
                            ))}
                    </div>
                </section>

                {/* Acceptance form */}
                {!isAccepted && !isExpired && (
                    <section className="mb-10">
                        <SectionTitle>Accept &amp; Sign</SectionTitle>
                        <form onSubmit={submit} className="bg-white rounded-2xl border border-[color:var(--bm-border)] p-6 md:p-8" data-testid="acceptance-form">
                            {!selectedOption && (
                                <div className="mb-5 p-3 rounded-md bg-[color:var(--bm-bg-mute)] text-[12.5px] text-[color:var(--bm-text-muted)]">
                                    Select one of the commercial options above, then complete the form below.
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Fld label="Full name *"   value={acceptance.signer_name}    onChange={v => setAcceptance({ ...acceptance, signer_name: v })}    testId="acc-name" />
                                <Fld label="Job title"      value={acceptance.signer_title}   onChange={v => setAcceptance({ ...acceptance, signer_title: v })}   testId="acc-title" />
                                <Fld label="Email *"        value={acceptance.signer_email}   onChange={v => setAcceptance({ ...acceptance, signer_email: v })}   type="email" testId="acc-email" />
                                <Fld label="Company"        value={acceptance.signer_company} onChange={v => setAcceptance({ ...acceptance, signer_company: v })} testId="acc-company" />
                            </div>

                            <div className="mt-5">
                                <Label className="text-[12.5px] font-medium text-[color:var(--bm-text-muted)]">Signature *</Label>
                                <div className="mt-2">
                                    <SignaturePad
                                        onDrawnChange={(dataUrl) => setAcceptance(a => ({ ...a, signature_image_base64: dataUrl }))}
                                        onTypedChange={(v) => setAcceptance(a => ({ ...a, typed_signature: v }))}
                                        typedValue={acceptance.typed_signature}
                                    />
                                    <div className="mt-2">
                                        <Label className="text-[11.5px] text-[color:var(--bm-text-subtle)]">Typed signature for the record *</Label>
                                        <Input value={acceptance.typed_signature} onChange={(e) => setAcceptance({ ...acceptance, typed_signature: e.target.value })} placeholder="Type your full name" className="mt-1 h-10" data-testid="acc-typed" />
                                    </div>
                                </div>
                            </div>

                            <label className="mt-5 flex items-start gap-3 p-4 rounded-lg bg-[color:var(--bm-bg-mute)] cursor-pointer">
                                <Checkbox checked={acceptance.accept_terms} onCheckedChange={(v) => setAcceptance({ ...acceptance, accept_terms: !!v })} className="mt-0.5" data-testid="acc-terms-checkbox" />
                                <span className="text-[12.5px] leading-relaxed text-[color:var(--bm-text)]">
                                    I confirm I have authority to bind <span className="font-semibold">{acceptance.signer_company || client.company_name}</span> to this agreement and I accept the commercial option selected and the terms set out in this proposal.
                                </span>
                            </label>

                            <div className="mt-6 flex items-center gap-3 justify-between flex-wrap">
                                <div className="flex items-center gap-1.5 text-[11.5px] text-[color:var(--bm-text-subtle)]">
                                    <Lock size={12} /> Signed timestamp, IP and device are recorded for audit.
                                </div>
                                <Button type="submit" disabled={submitting} className="h-11 px-6 text-[14px] font-medium" style={{ background: 'var(--bm-accent-600)' }} data-testid="acc-submit-btn">
                                    <ShieldCheck size={16} className="mr-1.5" /> {submitting ? 'Submitting…' : 'Accept & Sign'}
                                </Button>
                            </div>
                        </form>
                    </section>
                )}

                {/* Terms */}
                {settings.default_proposal_terms && (
                    <section className="mb-10">
                        <SectionTitle>Terms</SectionTitle>
                        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 text-[13px] text-[color:var(--bm-text-muted)] leading-relaxed whitespace-pre-wrap">
                            {settings.default_proposal_terms}
                        </div>
                    </section>
                )}
                {settings.default_contract_terms && !isAccepted && (
                    <section className="mb-10">
                        <SectionTitle>Agreement terms</SectionTitle>
                        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 text-[12.5px] text-[color:var(--bm-text-muted)] leading-relaxed whitespace-pre-wrap max-h-[360px] overflow-y-auto">
                            {settings.default_contract_terms}
                        </div>
                    </section>
                )}

                <footer className="text-center text-[11.5px] text-[color:var(--bm-text-subtle)] mt-12 pb-4">
                    {settings.company_name || 'Beyond Measure'} · This link is unique to {client.company_name}.
                </footer>
            </main>
        </div>
    );
}

function AcceptedBanner({ result, token, proposal }) {
    const contractNumber = result?.contract_number;
    const pdfUrl = `${API_BASE}/public/proposals/${token}/contract-pdf`;
    return (
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-[color:var(--bm-accent-50)] to-white border border-[color:var(--bm-accent)]/30 shadow-sm bm-fade-in" data-testid="accepted-banner">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[color:var(--bm-accent-600)] text-white flex items-center justify-center shrink-0">
                    <CheckCircle2 size={22} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-[18px] font-semibold text-[color:var(--bm-primary)]">
                        {result ? 'Thank you — proposal accepted.' : 'This proposal has been accepted.'}
                    </div>
                    <div className="text-[13px] text-[color:var(--bm-text-muted)] mt-1">
                        {contractNumber ? <>Your signed agreement <span className="font-semibold text-[color:var(--bm-text)]">{contractNumber}</span> is ready to download.</> : 'The signed agreement is available for download.'}
                        {proposal.accepted_at && <> · Signed {new Date(proposal.accepted_at).toLocaleString('en-GB')}.</>}
                    </div>
                    <div className="mt-4 flex gap-2">
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-white text-[13px] font-medium"
                           style={{ background: 'var(--bm-primary)' }}
                           data-testid="accepted-contract-pdf-btn">
                            <FileDown size={15} /> Download signed agreement
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PublicOptionCard({ opt, selected, onSelect, locked, addons, vatRate, hostingLine, proposalDate }) {
    const c = useMemo(
        () => computeOption(opt, vatRate, addons, proposalDate),
        [opt, vatRate, addons, proposalDate]
    );
    const baseSetup = Number(opt.setup_fee || 0);
    const baseMonthly = Number(opt.monthly_saas_fee || 0);
    const isAnnualUpfront = (opt.payment_frequency || 'monthly') === 'annual_upfront';
    const hostingActive = hostingLine && hostingLine.include_in_totals !== false;
    const hostingSetup = hostingActive ? Number(hostingLine.setup_fee || 0) : 0;
    const hostingMonthly = hostingActive ? Number(hostingLine.monthly_support_fee || 0) : 0;

    return (
        <button
            type="button"
            onClick={onSelect}
            disabled={locked}
            className={`text-left bg-white rounded-xl border overflow-hidden transition shadow-sm ${selected ? 'border-[color:var(--bm-accent)] ring-2 ring-[color:var(--bm-accent)]/40' : 'border-[color:var(--bm-border)] hover:shadow-md'} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
            data-testid={`public-option-${opt.id}`}
        >
            <div className="px-5 py-4 text-white" style={{ background: selected ? 'linear-gradient(135deg, var(--bm-accent-600) 0%, #0E4B73 100%)' : 'linear-gradient(135deg, var(--bm-primary) 0%, #0E4B73 100%)' }}>
                <div className="text-[11px] uppercase tracking-[0.14em] opacity-80 font-semibold">Year-one total including VAT</div>
                <div className="font-display text-[26px] font-semibold mt-1 leading-none" data-testid={`public-option-${opt.id}-total`}>{GBP(c.total_inc_vat)}</div>
                <div className="text-[11px] opacity-80 mt-1">{GBP(c.year_one_total_net)} excluding VAT</div>
            </div>
            <div className="p-5">
                <div className="font-display text-[15px] font-semibold text-[color:var(--bm-primary)]">{opt.name}</div>
                {opt.description && <div className="text-[12.5px] text-[color:var(--bm-text-muted)] mt-1">{opt.description}</div>}

                <div className="mt-4 space-y-2 text-[12.5px]">
                    {/* Monthly breakdown */}
                    {(hostingMonthly > 0 || (c.monthly_addons > 0 && baseMonthly !== c.monthly_total)) ? (
                        <>
                            <PubRow label="Base monthly SaaS" value={GBP(baseMonthly)} muted />
                            {c.monthly_addons - hostingMonthly > 0.009 && (
                                <PubRow label="Ad-hoc items" value={`+ ${GBP(c.monthly_addons - hostingMonthly)}`} muted />
                            )}
                            {hostingMonthly > 0 && (
                                <PubRow label={`Hosting — ${hostingLine.label}`} value={`+ ${GBP(hostingMonthly)}/month`} muted />
                            )}
                            <PubRow label="Monthly total" value={GBP(c.monthly_total)} strong />
                        </>
                    ) : (
                        <PubRow label="Monthly total" value={GBP(c.monthly_total)} strong />
                    )}

                    {/* Annual recurring */}
                    {c.discount_amount > 0 ? (
                        <>
                            <PubRow label="Annual recurring before discount" value={GBP(c.annual_total_gross)} muted />
                            <PubRow label="Discount" value={`− ${GBP(c.discount_amount)}`} accent />
                            <PubRow label="Annual recurring total" value={GBP(c.annual_total_net)} strong />
                        </>
                    ) : (
                        <PubRow label="Annual recurring total" value={GBP(c.annual_total_net)} />
                    )}

                    {/* Setup breakdown */}
                    {(hostingSetup > 0 || (c.setup_addons > 0 && baseSetup !== c.setup_total)) ? (
                        <>
                            <PubRow label="Base setup fee" value={GBP(baseSetup)} muted />
                            {c.setup_addons - hostingSetup > 0.009 && (
                                <PubRow label="Ad-hoc items" value={`+ ${GBP(c.setup_addons - hostingSetup)}`} muted />
                            )}
                            {hostingSetup > 0 && (
                                <PubRow label={`Hosting — ${hostingLine.label}`} value={`+ ${GBP(hostingSetup)}`} muted />
                            )}
                            <PubRow label="Setup total" value={GBP(c.setup_total)} strong />
                        </>
                    ) : (
                        <PubRow label="Setup total" value={GBP(c.setup_total)} />
                    )}
                    {c.deposit_due > 0 && <PubRow label="Deposit due" value={GBP(c.deposit_due)} muted />}

                    {/* Year-one totals */}
                    <div className="pt-2 border-t border-[color:var(--bm-border)] mt-2">
                        <PubRow label="Year-one total excluding VAT" value={GBP(c.year_one_total_net)} strong />
                        {c.vat_amount > 0 && <PubRow label="VAT" value={GBP(c.vat_amount)} muted />}
                        <PubRow label="Year-one total including VAT" value={GBP(c.total_inc_vat)} strong accent />
                        {isAnnualUpfront && (
                            <div className="mt-1 text-[10.5px] text-[color:var(--bm-text-subtle)] italic">
                                Annual upfront: Year-one total = Setup total + Annual recurring total
                            </div>
                        )}
                    </div>

                    <PubRow label="Contract length"   value={`${c.contract_length_months} months`} />
                    <PubRow label="Payment frequency" value={FREQUENCY[c.payment_frequency] || c.payment_frequency} />
                </div>
                {!locked && (
                    <div className={`mt-4 pt-3 border-t border-[color:var(--bm-border)] text-[12px] font-semibold ${selected ? 'text-[color:var(--bm-accent-600)]' : 'text-[color:var(--bm-text-subtle)]'}`}>
                        {selected ? '✓ Selected' : 'Click to select this option'}
                    </div>
                )}
            </div>
        </button>
    );
}

function PubRow({ label, value, accent, muted, strong }) {
    return (
        <div className="flex items-start justify-between gap-2">
            <span className={`${muted ? 'text-[color:var(--bm-text-subtle)]' : 'text-[color:var(--bm-text-muted)]'} shrink-0`}>{label}</span>
            <span className={`${strong ? 'font-semibold' : 'font-medium'} ${accent ? 'text-[color:var(--bm-accent-600)]' : (muted ? 'text-[color:var(--bm-text-muted)]' : 'text-[color:var(--bm-text)]')}`}>{value}</span>
        </div>
    );
}

function Row({ label, value, accent, sub }) {
    return (
        <div className="flex items-start justify-between gap-2">
            <span className="text-[color:var(--bm-text-muted)] shrink-0">{label}</span>
            <span className="text-right">
                <span className={`font-medium ${accent ? 'text-[color:var(--bm-accent-600)]' : 'text-[color:var(--bm-text)]'}`}>{value}</span>
                {sub && <div className="text-[10.5px] text-[color:var(--bm-text-subtle)] mt-0.5">{sub}</div>}
            </span>
        </div>
    );
}
function Fld({ label, value, onChange, type = 'text', testId }) {
    return (
        <div>
            <Label className="text-[12.5px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-10" data-testid={testId} />
        </div>
    );
}
function Meta({ label, value, sub }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className="font-semibold text-[color:var(--bm-text)] mt-0.5">{value}</div>
            {sub && <div>{sub}</div>}
        </div>
    );
}
function SectionTitle({ children }) {
    return <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[color:var(--bm-accent-600)] mb-4">{children}</div>;
}
function CenterMsg({ children }) {
    return <div className="min-h-screen flex items-center justify-center bg-[color:var(--bm-bg)]"><div className="text-center p-8">{children}</div></div>;
}

function HostingPicker({ options, selectedId, onSelect, locked, settings }) {
    return (
        <section className="mb-10" data-testid="public-hosting-section">
            <SectionTitle>Hosting &amp; Access — Choose one</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="public-hosting-picker">
                {options.map(h => {
                    const active = selectedId === h.id;
                    const includes = h.include_in_totals !== false;
                    const setup = Number(h.setup_fee || 0);
                    const monthly = Number(h.monthly_support_fee || 0);
                    const feeBadge = !includes
                        ? 'No extra fees'
                        : (setup === 0 && monthly === 0 ? 'Included' : `${GBP(setup)} setup · ${GBP(monthly)}/mo`);
                    return (
                        <button
                            key={h.id}
                            type="button"
                            onClick={() => !locked && onSelect(h.id)}
                            disabled={locked}
                            className={`text-left bg-white rounded-xl border overflow-hidden transition p-5 ${active ? 'border-[color:var(--bm-accent)] ring-2 ring-[color:var(--bm-accent)]/40 shadow-md' : 'border-[color:var(--bm-border)] hover:shadow-sm'} ${locked ? 'cursor-default' : 'cursor-pointer'}`}
                            data-testid={`public-hosting-option-${h.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="font-display text-[15px] font-semibold text-[color:var(--bm-primary)]">{h.label}</div>
                                {active && <CheckCircle2 size={18} className="text-[color:var(--bm-accent-600)] shrink-0" />}
                            </div>
                            {h.example_url && (
                                <div className="font-mono text-[12px] text-[color:var(--bm-text-muted)] mt-1 break-all">{h.example_url}</div>
                            )}
                            {h.description && (
                                <div className="text-[12px] text-[color:var(--bm-text-muted)] mt-2 leading-relaxed line-clamp-3">{h.description}</div>
                            )}
                            <div className="mt-3 inline-flex items-center rounded-md px-2 py-1 text-[11px] font-semibold bg-[color:var(--bm-accent-50)] text-[color:var(--bm-primary)]">
                                {feeBadge}
                            </div>
                            {h.notes && (
                                <div className="mt-2 text-[11px] text-[color:var(--bm-text-subtle)] italic">{h.notes}</div>
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="mt-3 text-[11.5px] text-[color:var(--bm-text-subtle)]">
                Totals on each commercial option update instantly based on your hosting choice.
            </div>
        </section>
    );
}
